import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AdminAuthService } from '../services/admin-auth.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const adminAuth = inject(AdminAuthService);

  return next(req).pipe(
    catchError((error) => {
      const currentUrl = router.url;
      const isPublicMemorialRoute = currentUrl.startsWith('/m/') || currentUrl.startsWith('/memorials/');
      const isAuthEndpoint = req.url.includes('/auth/');
      const isRefreshEndpoint = req.url.includes('/auth/refresh');
      const isGoogleEndpoint = req.url.includes('/auth/google');
      
      // Handle 401 Unauthorized
      if (error.status === 401) {
        // Don't redirect to login for public memorial routes
        if (isPublicMemorialRoute) {
          return throwError(() => error);
        }

        // Don't retry on Google OAuth endpoint or refresh endpoint
        if (isGoogleEndpoint || isRefreshEndpoint) {
          return throwError(() => error);
        }

        // Check if this is an admin route
        if (currentUrl.startsWith('/admin/')) {
          // For admin routes, try to refresh admin token
          return adminAuth.refreshToken().pipe(
            switchMap(() => {
              // Retry the original request with new token
              const clonedReq = req.clone();
              return next(clonedReq);
            }),
            catchError((refreshError) => {
              // Refresh failed, logout admin and redirect
              console.log('Admin token refresh failed, logging out');
              adminAuth.logout();
              router.navigate(['/admin/login']);
              return throwError(() => refreshError);
            })
          );
        } else {
          // For regular routes, try to refresh regular token
          return auth.refreshToken().pipe(
            switchMap(() => {
              // Retry the original request with new token
              const clonedReq = req.clone();
              return next(clonedReq);
            }),
            catchError((refreshError) => {
              // Refresh failed, logout and redirect
              console.log('Token refresh failed, logging out');
              auth.logout();
              router.navigate(['/auth/login']);
              return throwError(() => refreshError);
            })
          );
        }
      }
      
      // Handle 403 Forbidden - Account suspended
      if (error.status === 403 && error.error?.message === 'Account suspended') {
        if (currentUrl.startsWith('/admin/')) {
          adminAuth.logout();
          router.navigate(['/admin/login']);
        } else {
          auth.logout();
          router.navigate(['/auth/login']);
        }
      }
      
      // Handle 404 Not Found from auth endpoints (user deleted)
      if (error.status === 404 && isAuthEndpoint && !isPublicMemorialRoute) {
        // User not found = deleted user, logout and redirect to login
        auth.logout();
        router.navigate(['/auth/login']);
      }

      return throwError(() => error);
    })
  );
};
